import { STUDENT_INVITE_QUERY_PARAM, type Academy } from '@court-sync/shared';
import { buildAcademyLoginPath, buildAcademyPath, getAcademyHostMode } from '../../../lib/academyPaths';
import { buildOrgLoginPath, buildOrgPath, getOrgHostMode } from '../../../lib/orgPaths';
import { resolveOrgSlugFromPublicPath } from '../../../lib/resolveOrgBookingContext';

type AcademyRouteTarget = string | Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>;

function asAcademyFields(target: AcademyRouteTarget): Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'> {
  if (typeof target === 'string') {
    return { slug: target };
  }
  return target;
}

/** Append `studentInvite` when token present (keeps any existing query string on `path`). */
export function appendStudentInviteQuery(
  path: string,
  manualInviteToken: string | null | undefined
): string {
  const t = manualInviteToken?.trim();
  if (!t) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}${STUDENT_INVITE_QUERY_PARAM}=${encodeURIComponent(t)}`;
}

/** Append `invitation` and `studentInvite` when present — use on wizard internal navigations so EOI/register paths keep context. */
export function appendPublicBookingWizardContextQueries(
  path: string,
  opts: {
    invitationToken?: string | null;
    manualInviteToken?: string | null;
  }
): string {
  let next = path;
  const inv = opts.invitationToken?.trim();
  if (inv) {
    const sep = next.includes('?') ? '&' : '?';
    next = `${next}${sep}invitation=${encodeURIComponent(inv)}`;
  }
  return appendStudentInviteQuery(next, opts.manualInviteToken);
}

/** Prefer URL param, then persisted store token (for navigations before effects sync). */
export function resolveManualInviteTokenForNavigation(
  searchParams: URLSearchParams,
  storeToken: string | null | undefined
): string | null {
  const fromUrl = searchParams.get(STUDENT_INVITE_QUERY_PARAM)?.trim();
  if (fromUrl) return fromUrl;
  const fromStore = storeToken?.trim();
  return fromStore || null;
}

/** Clone search params for filter updates so `studentInvite` is not dropped. */
export function cloneSearchParamsPreservingStudentInvite(
  source: URLSearchParams,
  fallbackManualInviteToken: string | null | undefined
): URLSearchParams {
  const next = new URLSearchParams(source);
  const token =
    source.get(STUDENT_INVITE_QUERY_PARAM)?.trim() ||
    fallbackManualInviteToken?.trim();
  if (token) next.set(STUDENT_INVITE_QUERY_PARAM, token);
  else next.delete(STUDENT_INVITE_QUERY_PARAM);
  return next;
}

/** Private lesson booking wizard (guest / guardian). */
export function privateBookingBasePath(academy: AcademyRouteTarget): string {
  return buildAcademyPath(asAcademyFields(academy), '/book-private');
}

export function privateBookingRegisterPath(academy: AcademyRouteTarget): string {
  return `${privateBookingBasePath(academy)}/register`;
}

/** Base path for public lesson booking wizard (no trailing slash). */
export function publicBookingBasePath(academy: AcademyRouteTarget, lessonId: string): string {
  const orgSlug = resolveOrgSlugFromPublicPath();
  if (orgSlug !== null) {
    return buildOrgPath({ slug: orgSlug || 'org' }, `/book/${lessonId}`, getOrgHostMode());
  }
  return buildAcademyPath(asAcademyFields(academy), `/book/${lessonId}`, getAcademyHostMode());
}

export type PublicBookingWizardSegment =
  | 'register'
  | 'signup'
  | 'sessions'
  | 'eoi'
  | 'details'
  | 'payment'
  | 'success';

export function publicBookingStepPath(
  academy: AcademyRouteTarget,
  lessonId: string,
  segment: PublicBookingWizardSegment
): string {
  return `${publicBookingBasePath(academy, lessonId)}/${segment}`;
}

/** Booking URL for `returnUrl` after academy login — preserves invitation / waitlist / manual-invite query params. */
export function publicBookingReturnUrlForLogin(
  academy: AcademyRouteTarget,
  lessonId: string,
  options?: {
    invitationToken?: string | null;
    waitlistEntryId?: string | null;
    manualInviteToken?: string | null;
  }
): string {
  const base = publicBookingBasePath(academy, lessonId);
  const qs = new URLSearchParams();
  if (options?.invitationToken) qs.set('invitation', options.invitationToken);
  if (options?.manualInviteToken) qs.set(STUDENT_INVITE_QUERY_PARAM, options.manualInviteToken);
  if (options?.waitlistEntryId) {
    qs.set('waitlistClaim', '1');
    qs.set('entryId', options.waitlistEntryId);
  }
  return qs.toString() ? `${base}?${qs.toString()}` : base;
}

/** Path to academy login with encoded `returnUrl` back to this booking (with optional query params). */
export function academyBookingLoginPath(
  academy: AcademyRouteTarget,
  lessonId: string,
  options?: {
    invitationToken?: string | null;
    waitlistEntryId?: string | null;
    manualInviteToken?: string | null;
  }
): string {
  const returnPath = publicBookingReturnUrlForLogin(academy, lessonId, options);
  const orgSlug = resolveOrgSlugFromPublicPath();
  if (orgSlug !== null) {
    return buildOrgLoginPath({ slug: orgSlug || 'org' }, returnPath, getOrgHostMode());
  }
  return buildAcademyLoginPath(asAcademyFields(academy), returnPath, getAcademyHostMode());
}
